import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  FaPlus, FaEdit, FaTrash, FaEye, FaPause, FaPlay,
  FaBuilding, FaUser, FaMapMarkerAlt, FaClock, FaUsers,
  FaArrowRight, FaSpinner
} from 'react-icons/fa';

export default function MyTalentAds() {
  const navigate = useNavigate();
  const { API, user } = useContext(AppContext);
  
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchMyAds();
  }, [user]);

  const fetchMyAds = async () => {
    try {
      const response = await axios.get(`${API}/talents/my?user_id=${user.id}`);
      setAds(response.data);
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (adId, newStatus) => {
    try {
      await axios.put(`${API}/talents/${adId}/status?status=${newStatus}&user_id=${user.id}`);
      toast.success(`Annonce ${newStatus === 'active' ? 'activée' : newStatus === 'paused' ? 'mise en pause' : 'clôturée'}`);
      fetchMyAds();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Supprimer cette annonce définitivement ?')) return;
    
    try {
      await axios.delete(`${API}/talents/${adId}?user_id=${user.id}`);
      toast.success('Annonce supprimée');
      fetchMyAds();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getFilteredAds = () => {
    if (activeTab === 'all') return ads;
    return ads.filter(ad => ad.status === activeTab);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-orange-500">En attente de validation</Badge>;
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-500">En pause</Badge>;
      case 'closed':
        return <Badge className="bg-gray-500">Clôturée</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500">Refusée</Badge>;
      case 'expired':
        return <Badge className="bg-red-500">Expirée</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#635BFF]" />
      </div>
    );
  }

  const activeCount = ads.filter(a => a.status === 'active').length;
  const pendingCount = ads.filter(a => a.status === 'pending').length;
  const pausedCount = ads.filter(a => a.status === 'paused').length;
  const closedCount = ads.filter(a => a.status === 'closed' || a.status === 'expired').length;
  const rejectedCount = ads.filter(a => a.status === 'rejected').length;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540]">Mes annonces Talents</h1>
            <p className="text-gray-600">Gérez vos offres d'emploi et profils</p>
          </div>
          <Link to="/talents/publier">
            <Button className="bg-[#635BFF] hover:bg-[#5246E5]">
              <FaPlus className="mr-2" /> Nouvelle annonce
            </Button>
          </Link>
        </div>

        {/* Pending Alert */}
        {pendingCount > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <p className="text-orange-700 flex items-center gap-2">
              <span className="text-xl">⏳</span>
              <span><strong>{pendingCount} annonce(s)</strong> en attente de validation par l'équipe</span>
            </p>
          </div>
        )}

        {/* Rejected Alert */}
        {rejectedCount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 flex items-center gap-2">
              <span className="text-xl">❌</span>
              <span><strong>{rejectedCount} annonce(s)</strong> refusée(s) - Consultez les raisons ci-dessous</span>
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="cursor-pointer" onClick={() => setActiveTab('pending')}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-orange-600">{pendingCount}</p>
              <p className="text-sm text-gray-500">En attente</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => setActiveTab('active')}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{activeCount}</p>
              <p className="text-sm text-gray-500">Actives</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => setActiveTab('paused')}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">{pausedCount}</p>
              <p className="text-sm text-gray-500">En pause</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => setActiveTab('closed')}>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-600">{closedCount}</p>
              <p className="text-sm text-gray-500">Clôturées</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="all">Toutes ({ads.length})</TabsTrigger>
            <TabsTrigger value="pending">En attente ({pendingCount})</TabsTrigger>
            <TabsTrigger value="active">Actives ({activeCount})</TabsTrigger>
            <TabsTrigger value="rejected">Refusées ({rejectedCount})</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {getFilteredAds().length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FaUsers className="text-6xl text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[#0A2540] mb-2">
                    Aucune annonce
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Publiez votre première annonce pour trouver des talents ou des opportunités
                  </p>
                  <Link to="/talents/publier">
                    <Button className="bg-[#635BFF] hover:bg-[#5246E5]">
                      <FaPlus className="mr-2" /> Publier une annonce
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {getFilteredAds().map((ad) => (
                  <Card key={ad.id} className="hover:shadow-md transition-all">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Icon */}
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          ad.ad_type === 'job_offer' 
                            ? 'bg-[#635BFF]/10' 
                            : 'bg-[#00D4FF]/10'
                        }`}>
                          {ad.ad_type === 'job_offer' ? (
                            <FaBuilding className="text-2xl text-[#635BFF]" />
                          ) : (
                            <FaUser className="text-2xl text-[#00D4FF]" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {getStatusBadge(ad.status)}
                            <Badge variant="outline">
                              {ad.ad_type === 'job_offer' ? 'Offre' : 'Profil'}
                            </Badge>
                          </div>

                          <h3 className="text-lg font-semibold text-[#0A2540] mb-1">
                            {ad.title}
                          </h3>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                            {ad.location && (
                              <span className="flex items-center gap-1">
                                <FaMapMarkerAlt /> {ad.location}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <FaClock /> {formatDate(ad.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <FaEye /> {ad.views_count} vues
                            </span>
                            <span className="flex items-center gap-1">
                              <FaUsers /> {ad.applications_count} candidatures
                            </span>
                          </div>

                          {/* Rejection reason */}
                          {ad.status === 'rejected' && ad.rejection_reason && (
                            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700 mb-2">
                              <strong>Raison du refus:</strong> {ad.rejection_reason}
                            </div>
                          )}

                          {/* Pending message */}
                          {ad.status === 'pending' && (
                            <div className="bg-orange-50 border border-orange-200 rounded p-2 text-sm text-orange-700 mb-2">
                              Votre annonce est en cours de validation par l'équipe. Vous recevrez une notification.
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap gap-2">
                            {/* View button - always visible */}
                            <Link to={`/talents/${ad.id}`}>
                              <Button variant="outline" size="sm">
                                <FaEye className="mr-1" /> Voir
                              </Button>
                            </Link>
                            
                            {/* Pause button - for active ads */}
                            {ad.status === 'active' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleStatusChange(ad.id, 'paused')}
                              >
                                <FaPause className="mr-1" /> Pause
                              </Button>
                            )}
                            
                            {/* Reactivate button - for paused ads */}
                            {ad.status === 'paused' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-green-600 border-green-600"
                                onClick={() => handleStatusChange(ad.id, 'active')}
                              >
                                <FaPlay className="mr-1" /> Réactiver
                              </Button>
                            )}
                            
                            {/* Close button - for active or paused ads */}
                            {(ad.status === 'active' || ad.status === 'paused') && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleStatusChange(ad.id, 'closed')}
                              >
                                Clôturer
                              </Button>
                            )}
                          </div>
                          
                          {/* Delete button - always available */}
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50 w-fit"
                            onClick={() => handleDelete(ad.id)}
                          >
                            <FaTrash className="mr-1" /> Supprimer
                          </Button>
                        </div>
                      </div>

                      {/* Applications preview */}
                      {ad.applications_count > 0 && ad.status === 'active' && (
                        <div className="mt-4 pt-4 border-t">
                          <Link 
                            to={`/talents/${ad.id}`} 
                            className="text-[#635BFF] text-sm font-medium flex items-center gap-1 hover:underline"
                          >
                            Voir les {ad.applications_count} candidature(s) <FaArrowRight />
                          </Link>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
